import type { BaseCurrencyPriceEntry } from "@/backend/deso";
import { RESERVED_DESO_NANOS_FOR_FEES } from "@/utils/constants";

export const CURRENCY_TICKER = {
  DESO: "DESO",
  DUSD: "DUSD",
  FOCUS: "FOCUS",
};

export const MAX_DESO_BALANCE_VALUE_TO_SEND: number = -1;
export const DUSD_DEFAULT_EXCHANGE_RATE_USD_CENTS = 100;

export function formatUSD(
  usdAmount: number | string,
  withDecimal = true,
  numDecimals = 2,
  lessSign = "< ",
) {
  const number = Number(usdAmount);
  if (number === 0) {
    return "$0.00";
  }

  const absNumber = Math.abs(number);
  const isNegative = number < 0;

  if (absNumber < 0.01 && withDecimal && numDecimals <= 2) {
    const sign = isNegative ? "-" : "";
    return `${lessSign}${sign}$0.01`;
  }

  return `${number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: withDecimal ? numDecimals : 0,
    minimumFractionDigits: withDecimal ? numDecimals : 0,
  })}`;
}

export function formatUSDCents(cents: number, numDecimals?: number) {
  return formatUSD(cents / 100, true, numDecimals);
}

export function usdToCents(cents: number) {
  return cents * 100;
}

export function centsToUSD(cents: number) {
  return cents / 100;
}

export function nanosToInteger(nanos: number | string | bigint) {
  return Number(nanos) / 1e9;
}

export function tokenNanosToInteger(nanos: number | string | bigint) {
  return Number(nanos) / 1e18;
}

export function integerToTokenNanos(nanos: number | string | bigint) {
  return Number(nanos) * 1e18;
}

export function dateNanosToInteger(nanos: number | string | bigint) {
  return Math.trunc(Number(nanos) / 1_000_000);
}

export function integerToNanos(nanos: number | string | bigint) {
  return Math.floor(Number(nanos) * 1e9);
}

export function integerToPercentage(value: number | string, percentage = 100) {
  return Number(value) / percentage;
}

export function integerToUSD(value: number | string, percentage = 100) {
  return Number(value) / percentage;
}

export function percentageToInteger(value: number | string, percentage = 100) {
  return Number(value) * percentage;
}

export function centsToUsd(value: number | string, decimals = 100) {
  return Number(value) * decimals;
}

export function convertToRoundedPercentage(
  value: number | string,
  percentage = 100,
  fixed = 2,
) {
  return Number((Number(value) * percentage).toFixed(fixed));
}

export function desoToUSD(desoAmount: number | string, xRate: number) {
  return Number(desoAmount) * (xRate / 100);
}

export function usdToDeso(usdAmount: number | string, xRate: number) {
  return Number(usdAmount) / (xRate / 100);
}

export function isMoreOrEqualThanAPeriod(
  nanos: number | string | bigint,
  period?: number | string | bigint,
): boolean {
  const nanosInAYear = BigInt(period || 31536000000000000);
  const nanosValue = BigInt(nanos);

  return nanosValue >= nanosInAYear;
}

export function focusToUsd(focusAmount: number | string, xRate: number) {
  return Number(focusAmount) * xRate;
}

export function usdToFocus(usdAmount: number | string, xRate: number) {
  return Number(usdAmount) / xRate;
}

export function tokenToUsd(focusAmount: number | string, xRate: number) {
  return Number(focusAmount) * xRate;
}

export function usdToToken(focusAmount: number | string, xRate: number) {
  return Number(focusAmount) / xRate;
}

export function formatDecimalValue(
  value: string | number,
  maxDecimalPlaces = 2,
  minDecimalPlaces = 0,
) {
  const minDisplayableValue =
    maxDecimalPlaces > 0 ? `0.${"0".repeat(maxDecimalPlaces - 1)}1` : "1";

  if (Number(value) > 0 && Number(value) < Number(minDisplayableValue)) {
    return `< ${minDisplayableValue}`;
  }

  return Number(value).toLocaleString("en-US", {
    maximumFractionDigits: maxDecimalPlaces,
    minimumFractionDigits: minDecimalPlaces,
  });
}

export function numToHexString(num: number) {
  return `0x${num.toString(16)}`;
}

export const getDepositTickerAlert = (ticker: string) => {
  const networkType =
    process.env.NEXT_PUBLIC_IS_TESTNET === "true" ? "testnet" : "mainnet";
  switch (ticker) {
    case "USDC":
      return `Note: This address only accepts ${networkType} USDC ERC-20 tokens sent from an Ethereum wallet. Do not send USDC from other networks, side chains, ZK-rollups, wrapped USDC, etc. or it will result in a loss of funds.`;
    case "ETH":
      return `Note: This address only accepts ${networkType} ETH sent from an Ethereum wallet or exchange. Do not send ETH from other networks, side chains, ZK-rollups, wrapped ETH, etc. or it will result in a loss of funds.`;
    case "SOL":
      return `Note: This address only accepts ${networkType} SOL sent from a Solana wallet. Do not send SOL from other networks, SPL tokens, wrapped SOL, etc. or it will result in a loss of funds.`;
    case "USDT":
      return `Note: This address only accepts ${networkType} USDT ERC-20 tokens sent from an Ethereum wallet. Do not send USDT from other networks, side chains, ZK-rollups, wrapped USDT, etc. or it will result in a loss of funds.`;
    case "BTC":
      return `Note: This address only accepts ${networkType} BTC sent from a Bitcoin wallet. Do not send BTC from other networks, wrapped BTC, etc. or it will result in a loss of funds.`;
    case "USDC-SOL":
      return `Note: This address only accepts mainnet USDC tokens sent from a Solana wallet. Do not send USDC from other networks, side chains, ZK-rollups, wrapped USDC, etc. or it will result in a loss of funds.`;
    default:
      return "";
  }
};

export const getDesoAmountToSend = (amount: number, balanceNanos: number) => {
  const amountNanos = integerToNanos(amount);
  const isMaxBalanceSelected = amountNanos >= balanceNanos;

  return isMaxBalanceSelected
    ? balanceNanos - RESERVED_DESO_NANOS_FOR_FEES
    : amountNanos;
};

export const percentToBasisPoints = (percentDecimalValue: number | string) => {
  return Number(percentDecimalValue) * 100;
};

export const percentToBasisPointsRounded = (
  percentDecimalValue: number | string,
) => {
  return Math.round(Number(percentDecimalValue) * 100);
};

export const basisPointsToPercent = (percentDecimalValue: number | string) => {
  return Number(percentDecimalValue) / 100;
};

export const formatTruncatedValue = (
  value: number | string,
  percentage = 1,
) => {
  return Math.floor(Number(value) * percentage * 100) / 100;
};

export const getVisiblePrice = (coinPrice: BaseCurrencyPriceEntry) => {
  const liquidPrice = [
    coinPrice.MidPriceInUsd,
    coinPrice.BestAskInUsd,
    coinPrice.BestBidInUsd,
  ]
    .map(Number)
    .find((price) => price !== 0);

  return liquidPrice || 0;
};
